import { useInfiniteQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { MessageSquareIcon, Sparkles } from "lucide-react";

import { ShowcaseCard, ShowcaseCardSkeleton, type ShowcaseItem } from "@/components/index/showcase";
import { Empty, EmptyDescription, EmptyMedia, EmptyTitle } from "@/components/ui/empty";
import { featuredThreadsInfiniteQueryOptions } from "@/hooks/query/thread";
import { createWebPageJsonLd, seo } from "@/lib/seo";
import * as m from "@/paraglide/messages";

const SKELETON_KEYS = ["s1", "s2", "s3", "s4", "s5", "s6", "s7", "s8", "s9"] as const;

export const Route = createFileRoute("/featured/")({
	component: FeaturedPage,
	head: ({ match }) => {
		const title = m.featured_pageTitle();
		const description = m.featured_metaDescription();

		return seo({
			title,
			description,
			pathname: match.pathname,
			structuredData: createWebPageJsonLd({
				title,
				description,
				pathname: match.pathname,
			}),
		});
	},
});

type FeaturedThread = {
	id: string;
	title: string | null;
	summary: string | null;
	prompt: string | null;
	createdAt: Date;
};

function FeaturedPage() {
	const { data: featuredData, isLoading } = useInfiniteQuery(
		featuredThreadsInfiniteQueryOptions({
			pageSize: 24,
		}),
	);

	const items: ShowcaseItem[] =
		featuredData?.pages.flatMap((page) =>
			(page.data as FeaturedThread[]).map((thread) => ({
				id: thread.id,
				title: thread.title ?? m.common_unnamedThread(),
				summary: thread.summary,
				prompt: thread.prompt,
				createdAt: thread.createdAt,
			})),
		) ?? [];

	return (
		<main className="mx-auto max-w-6xl px-6 py-12">
			{/* Header */}
			<div className="text-center mb-12">
				<div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-accent border border-border/60 text-sm text-accent-foreground mb-5">
					<Sparkles className="w-4 h-4 text-primary" />
					{m.featured_badge()}
				</div>
				<h1 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
					{m.featured_heading()}
				</h1>
				<p className="text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
					{m.featured_metaDescription()}
				</p>
			</div>

			{/* Grid */}
			{isLoading ? (
				<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
					{SKELETON_KEYS.map((key) => (
						<ShowcaseCardSkeleton key={key} />
					))}
				</div>
			) : items.length > 0 ? (
				<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
					{items.map((item, i) => (
						<ShowcaseCard key={item.id} item={item} index={i} />
					))}
				</div>
			) : (
				<Empty>
					<EmptyMedia variant="icon">
						<MessageSquareIcon />
					</EmptyMedia>
					<EmptyTitle>{m.featured_noThreads()}</EmptyTitle>
					<EmptyDescription>{m.featured_noThreadsDesc()}</EmptyDescription>
				</Empty>
			)}
		</main>
	);
}
